import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteCampaignPromoCode } from 'api/campaigns';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { PromoCode } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeletePromoCodeAlertProps extends AlertDialog.AlertDialogRootProps {
    promoCode: PromoCode;
}

const DeletePromoCodeAlert = (props: DeletePromoCodeAlertProps) => {
    const toast = useToast();
    const { promoCode, children, ...rest } = props;

    const { campaign_id } = useParams<{ campaign_id: string }>();
    const campaignId = parseInt(campaign_id);

    const queryClient = useQueryClient();

    const deletePromoCodeMutation = useMutation({
        mutationFn: () => deleteCampaignPromoCode(campaignId, promoCode.id),
        onSuccess: () => {
            toast.success('Promo code deleted.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.promoCodes(campaignId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                typeof error.response?.data === 'string'
                    ? error.response.data
                    : 'Unable to delete promo code.',
            );
        },
    });

    return (
        <AlertDialog.Root type="error" {...rest}>
            <AlertDialog.Trigger asChild>{children}</AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <div className={'mb-8'}>
                            <AlertDialog.Title>
                                Delete <em>{promoCode.code}</em>?
                            </AlertDialog.Title>
                            <AlertDialog.Description>
                                This cannot be undone. Are you sure?
                            </AlertDialog.Description>
                        </div>

                        <div className={'flex items-center justify-between gap-x-2'}>
                            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                            <AlertDialog.Action onClick={() => deletePromoCodeMutation.mutate()}>
                                Delete
                            </AlertDialog.Action>
                        </div>
                    </AlertDialog.Body>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { DeletePromoCodeAlert };
